import React, { useEffect, useState } from 'react';
import css from './AppointmentInfoStepPresenter.module.css';
import { WizardStepWrapper } from '../step';
import {
  H2,
  IndicatesRequired,
  LoadingSpinner,
  LoadingSpinnerSize,
  RadioCardGroup,
  RadioCardOption,
  RadioCardOptionOrientation,
  StackedColumn,
  StackedColumnSpacing,
} from '../../../../core';
import {
  AppointmentInfo,
  AppointmentInfoQuestionAnswers,
  AppointmentType,
  AppointmentTypeQuestion,
  SchedulingDetails,
  findAppointmentTypeInList,
} from '../../../../api/models';
import { AppointmentInfoQuestion } from './appointmentInfoQuestion';
import { PresenterState } from './PresenterState';
import { GenericError } from '../../../genericError';
import NoAppointmentTypesAlertDialog from './appointmentInfoStepAlertDialog/noAppointmentTypesAlertDialog';

export interface AppointmentInfoStepPresenterProps {
  schedulingDetails: SchedulingDetails;
  state: PresenterState;
  onAppointmentTypeSelected?: (appointmentTypeId: string) => void;
  onContinue: (appointmentInfo: AppointmentInfo) => void;
  onNoAppointmentTypesRedirect: () => void;
}

const AppointmentInfoStepPresenter: React.FC<
  AppointmentInfoStepPresenterProps
> = (props: AppointmentInfoStepPresenterProps) => {
  const [appointmentTypeOptions, setAppointmentTypeOptions] = useState<
    RadioCardOption[]
  >([]);
  const [appointmentType, setAppointmentType] = useState<
    AppointmentType | undefined
  >(
    findAppointmentTypeInList(
      props.schedulingDetails.providerDetails?.appointmentTypeList,
      props.schedulingDetails.appointmentInfo?.appointmentTypeId
    )
  );
  const [questionList, setQuestionList] = useState<AppointmentTypeQuestion[]>(
    []
  );
  const [questionAnswers, setQuestionAnswers] =
    useState<AppointmentInfoQuestionAnswers[]>();
  const [validate, setValidate] = useState<boolean>(false);

  useEffect(() => {
    setAppointmentTypeOptions(
      props.schedulingDetails.providerDetails?.appointmentTypeList
        ? props.schedulingDetails.providerDetails.appointmentTypeList.map(
            (apptType) => {
              return {
                value: apptType.id ?? '',
                label: apptType.name ?? '',
                description: apptType.description ?? '',
              };
            }
          )
        : []
    );
  }, [props.schedulingDetails.providerDetails?.appointmentTypeList]);

  useEffect(() => {
    if (appointmentType === undefined) {
      setAppointmentType(
        findAppointmentTypeInList(
          props.schedulingDetails.providerDetails?.appointmentTypeList,
          props.schedulingDetails.appointmentInfo?.appointmentTypeId
        )
      );
    }

    if (
      questionAnswers === undefined &&
      props.schedulingDetails.appointmentInfo?.questionAnswers
    ) {
      setQuestionAnswers(
        props.schedulingDetails.appointmentInfo?.questionAnswers
      );
    } else if (questionAnswers === undefined) {
      const answers = appointmentType?.questions
        ?.map((question) => {
          return question.answerDefault
            ? {
                questionId: question.questionId,
                questionAnswerId: question.answerDefault,
              }
            : null;
        })
        .filter((answer) => answer !== null);
      setQuestionAnswers(answers as AppointmentInfoQuestionAnswers[]);
    }
  }, [
    props.schedulingDetails.providerDetails?.appointmentTypeList,
    props.schedulingDetails.appointmentInfo,
    appointmentType,
    questionAnswers,
  ]);

  useEffect(() => {
    const foundAppointmentType = findAppointmentTypeInList(
      props.schedulingDetails.providerDetails?.appointmentTypeList,
      appointmentType?.id
    );

    setAppointmentType(foundAppointmentType);
    setQuestionList(foundAppointmentType?.questions ?? []);
  }, [
    appointmentType?.id,
    props.schedulingDetails.providerDetails?.appointmentTypeList,
  ]);

  const onContinue = () => {
    if (isValid()) {
      props.onContinue({
        appointmentTypeId: appointmentType?.id,
        appointmentTypeName: appointmentType?.name,
        questionAnswers: questionAnswers,
        commentRequired: appointmentType?.requireComment,
      });
    } else {
      setValidate(true);
    }
  };

  // return false to cancel onContinue
  const isValid = (): boolean => {
    return (
      appointmentType !== undefined &&
      questionList.every(
        (question) => !question.required || isQuestionAnswered(question)
      ) &&
      true
    );
  };

  const isQuestionAnswered = (question: AppointmentTypeQuestion): boolean => {
    const answer = questionAnswers?.find(
      (answer) => answer.questionId === question.questionId
    );
    return (
      answer !== undefined &&
      answer.questionAnswerId !== undefined &&
      answer.questionAnswerId.length > 0
    );
  };

  const onAppointmentTypeChanged = (appointmentTypeId: string) => {
    setAppointmentType(
      findAppointmentTypeInList(
        props.schedulingDetails.providerDetails?.appointmentTypeList,
        appointmentTypeId
      )
    );
    setQuestionAnswers(undefined);
    setValidate(false);
    props.onAppointmentTypeSelected &&
      props.onAppointmentTypeSelected(appointmentTypeId);
  };

  const onQuestionAnswerChanged = (
    questionId: string | undefined,
    answerId: string
  ) => {
    const answers = questionAnswers
      ? questionAnswers.filter((answer) => answer.questionId !== questionId)
      : [];
    answers.push({
      questionId: questionId,
      questionAnswerId: answerId,
    });

    setQuestionAnswers(answers);
  };

  return (
    <WizardStepWrapper
      onContinue={
        props.state === PresenterState.Ready && appointmentType !== undefined
          ? onContinue
          : undefined
      }
      stickyWhenNarrow={true}
    >
      <StackedColumn spacing={StackedColumnSpacing.Wide}>
        <StackedColumn>
          {props.state === PresenterState.Error ? <GenericError /> : null}
          <H2 text="What kind of appointment are you scheduling?" />
          <IndicatesRequired />
        </StackedColumn>
        <StackedColumn spacing={StackedColumnSpacing.Wider}>
          {props.state === PresenterState.LoadingAppointmentTypes ? (
            <LoadingSpinner
              label="Loading, please wait..."
              size={LoadingSpinnerSize.Medium}
            />
          ) : props.state === PresenterState.NoAppointmentTypes ? (
            <NoAppointmentTypesAlertDialog
              onDismiss={() => props.onNoAppointmentTypesRedirect()}
              isOpen={true}
            />
          ) : (
            <RadioCardGroup
              label="Reason for appointment"
              groupName="appt_type"
              required={true}
              validate={validate}
              radioOptions={appointmentTypeOptions}
              selectedValue={appointmentType?.id ?? ''}
              orientation={RadioCardOptionOrientation.Wrap}
              onChange={(value) => onAppointmentTypeChanged(value)}
            />
          )}
          <div className={css.questionContainer}>
            {props.state === PresenterState.LoadingQuestions ? (
              <LoadingSpinner
                label="Loading, please wait..."
                size={LoadingSpinnerSize.Medium}
              />
            ) : (
              <StackedColumn spacing={StackedColumnSpacing.Wide}>
                {questionList.map((question, index) => {
                  return (
                    <div key={index}>
                      <AppointmentInfoQuestion
                        question={question}
                        selectedAnswer={
                          questionAnswers?.find(
                            (answer) =>
                              answer.questionId === question.questionId
                          )?.questionAnswerId
                        }
                        validate={validate}
                        onAnswerChanged={(answerId) =>
                          onQuestionAnswerChanged(question.questionId, answerId)
                        }
                      />
                    </div>
                  );
                })}
              </StackedColumn>
            )}
          </div>
        </StackedColumn>
      </StackedColumn>
    </WizardStepWrapper>
  );
};

export default AppointmentInfoStepPresenter;
